/**
 * Frappe API Client Wrapper
 * Handles all communication with ERPNext/Frappe backend
 * 
 * This is the main orchestrator that delegates to specialized clients:
 * - AuthClient: Authentication
 * - ResourceClient: CRUD operations
 * - MethodClient: Method calls
 * - ResponseHandler: Response processing
 */

import { AuthClient } from "./frappe-client/auth/AuthClient";
import { ResourceClient } from "./frappe-client/resources/ResourceClient";
import { MethodClient } from "./frappe-client/methods/MethodClient";
// Types are defined locally below, no need to import

export interface FrappeClientConfig {
  url: string;
  apiKey?: string;
  apiSecret?: string;
  useSession?: boolean;
}

export interface FrappeListParams {
  doctype: string;
  filters?: any[] | Record<string, any>; // Support both array and object formats
  fields?: string[];
  limit_start?: number;
  limit_page_length?: number;
  order_by?: string;
}

export interface FrappeListResponse<T> {
  data: T[];
  total: number;
  pageLength: number;
}

export interface FrappeGetParams {
  doctype: string;
  name: string;
}

export interface FrappeCreateParams {
  doctype: string;
  data: Record<string, any>;
}

export interface FrappeUpdateParams {
  doctype: string;
  name: string;
  data: Record<string, any>;
}

export interface FrappeDeleteParams {
  doctype: string;
  name: string;
}

export class FrappeClient {
  private config: FrappeClientConfig;
  private authClient: AuthClient;
  private resourceClient: ResourceClient;
  private methodClient: MethodClient;

  constructor(config: FrappeClientConfig) {
    this.config = config;
    
    // Initialize specialized clients
    this.authClient = new AuthClient({
      url: config.url,
      useSession: config.useSession ?? false,
      apiKey: config.apiKey,
      apiSecret: config.apiSecret,
    });
    
    // MethodClient must be initialized first since ResourceClient depends on it
    this.methodClient = new MethodClient({
      url: config.url,
      getAuthHeaders: () => this.getAuthHeaders(),
    });
    
    this.resourceClient = new ResourceClient({
      url: config.url,
      getAuthHeaders: () => this.getAuthHeaders(),
      getCount: (doctype: string, filters?: Record<string, any>) =>
        this.methodClient.getCount({ doctype, filters }),
    });
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    // Delegate to authClient for CSRF token handling
    const headers = await this.authClient.getAuthHeaders();

    // Add API key authentication if configured
    if (this.config.apiKey && this.config.apiSecret) {
      const credentials = btoa(`${this.config.apiKey}:${this.config.apiSecret}`);
      headers["Authorization"] = `Basic ${credentials}`;
    }

    return headers;
  }

  /**
   * List documents of a doctype
   * @returns Array of documents (legacy method for backward compatibility)
   * Automatically handles CSRF and session errors
   */
  async list<T = any>(params: FrappeListParams): Promise<T[]> {
    try {
      return await this.resourceClient.list<T>(params);
    } catch (error) {
      const { handleSessionError } = await import("./frappe-client/errors/SessionErrorInterceptor");
      await handleSessionError(error);
      throw error;
    }
  }

  /**
   * List documents of a doctype with pagination metadata
   * @returns Response with data, total count, and page length
   * Automatically handles CSRF and session errors
   */
  async listWithPagination<T = any>(
    params: FrappeListParams
  ): Promise<FrappeListResponse<T>> {
    try {
      return await this.resourceClient.listWithPagination<T>(params);
    } catch (error) {
      const { handleSessionError } = await import("./frappe-client/errors/SessionErrorInterceptor");
      await handleSessionError(error);
      throw error;
    }
  }

  /**
   * Get a single document
   * Automatically handles CSRF and session errors
   */
  async get<T = any>(params: FrappeGetParams): Promise<T> {
    try {
      return await this.resourceClient.get<T>(params);
    } catch (error) {
      const { handleSessionError } = await import("./frappe-client/errors/SessionErrorInterceptor");
      await handleSessionError(error);
      throw error;
    }
  }

  /**
   * Create a new document
   * Automatically handles CSRF and session errors
   */
  async create<T = any>(params: FrappeCreateParams): Promise<T> {
    try {
      return await this.resourceClient.create<T>(params);
    } catch (error) {
      const { handleSessionError } = await import("./frappe-client/errors/SessionErrorInterceptor");
      await handleSessionError(error);
      throw error;
    }
  }

  /**
   * Update an existing document
   * Automatically handles CSRF and session errors
   */
  async update<T = any>(params: FrappeUpdateParams): Promise<T> {
    try {
      return await this.resourceClient.update<T>(params);
    } catch (error) {
      const { handleSessionError } = await import("./frappe-client/errors/SessionErrorInterceptor");
      await handleSessionError(error);
      throw error;
    }
  }

  /**
   * Delete a document
   * Automatically handles CSRF and session errors
   */
  async delete(params: FrappeDeleteParams): Promise<void> {
    try {
      return await this.resourceClient.delete(params);
    } catch (error) {
      const { handleSessionError } = await import("./frappe-client/errors/SessionErrorInterceptor");
      await handleSessionError(error);
      throw error;
    }
  }

  /**
   * Call a Frappe method (API endpoint)
   * Automatically handles CSRF and session errors
   */
  async call<T = any>(method: string, args?: Record<string, any>): Promise<T> {
    try {
      return await this.methodClient.call<T>({ method, args });
    } catch (error) {
      const { handleSessionError } = await import("./frappe-client/errors/SessionErrorInterceptor");
      await handleSessionError(error);
      throw error;
    }
  }

  /**
   * Get count of documents matching filters
   * Uses Frappe's get_count method as fallback when total count is not in list response
   */
  async getCount(doctype: string, filters?: Record<string, any>): Promise<number> {
    return this.methodClient.getCount({ doctype, filters });
  }

  /**
   * Login to get session (if using session-based auth)
   */
  async login(username: string, password: string): Promise<string> {
    return this.authClient.login(username, password);
  }

  /**
   * Logout (clear session)
   */
  async logout(): Promise<void> {
    return this.authClient.logout();
  }
}

// Create singleton instance
let frappeClientInstance: FrappeClient | null = null;

/**
 * Initialize the Frappe client
 */
export function initFrappeClient(config: FrappeClientConfig): FrappeClient {
  frappeClientInstance = new FrappeClient(config);
  return frappeClientInstance;
}

/**
 * Get the Frappe client instance
 */
export function getFrappeClient(): FrappeClient {
  if (!frappeClientInstance) {
    // Prefer explicit API URL if provided via env
    // For local dev (Vite), default to '/api' so that the Vite proxy can handle CORS & cookies
    let apiUrl = import.meta.env.VITE_FRAPPE_API_URL;
    if (!apiUrl) {
      if (import.meta.env.DEV) {
        // Use relative URL so Vite dev server proxies '/api' → 'http://localhost:8000'
        apiUrl = "/api";
      } else {
        // Production: use current origin (same-origin API calls)
        apiUrl = window.location.origin;
      }
    }
    const apiKey = import.meta.env.VITE_FRAPPE_API_KEY;
    const apiSecret = import.meta.env.VITE_FRAPPE_API_SECRET;
    
    frappeClientInstance = new FrappeClient({
      url: apiUrl,
      apiKey,
      apiSecret,
      useSession: !apiKey, // Use session if no API key provided
    });
  }
  return frappeClientInstance;
}

// Export convenience methods
export const frappe = {
  list: <T = any>(params: FrappeListParams) => getFrappeClient().list<T>(params),
  listWithPagination: <T = any>(params: FrappeListParams) => getFrappeClient().listWithPagination<T>(params),
  get: <T = any>(params: FrappeGetParams) => getFrappeClient().get<T>(params),
  create: <T = any>(params: FrappeCreateParams) => getFrappeClient().create<T>(params),
  update: <T = any>(params: FrappeUpdateParams) => getFrappeClient().update<T>(params),
  delete: (params: FrappeDeleteParams) => getFrappeClient().delete(params),
  call: <T = any>(method: string, args?: Record<string, any>) => getFrappeClient().call<T>(method, args),
  getCount: (doctype: string, filters?: Record<string, any>) => getFrappeClient().getCount(doctype, filters),
  login: (username: string, password: string) => getFrappeClient().login(username, password),
  logout: () => getFrappeClient().logout(),
};

